import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';

/**
 * RequireCustomerContext
 * ─────────────────────────────────────────────────────────────────────
 * Wraps routes that only make sense inside a customer's scope
 * (/recordings, /voicemails, /ivr/trees, etc.).
 *
 * Behaviour by role:
 *   - super_admin / reseller_owner:
 *       • With a customer context entered → render children as normal.
 *       • Without → bounce to /customers so they pick one first. This
 *         replaces the old "dialog asks which customer" flow.
 *   - Everyone else:
 *       • Renders children unchanged. office_manager / standard_user etc.
 *         have exactly one customer in their tenant scope — they don't
 *         need (or even have access to) the "enter a customer" flow.
 *
 * Do NOT wrap cross-customer surfaces (reseller list, carriers, platform
 * admin, plans, branding) in this guard — those are reseller / platform
 * tools that work across customers.
 */
export function RequireCustomerContext({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const context = useCustomerContext((s) => s.current);

  // Not logged in — the parent RequireAuth handles this; belt-and-braces.
  if (!user) return <Navigate to="/login" replace />;

  const needsContext = user.role === 'super_admin' || user.role === 'reseller_owner';
  if (needsContext && !context) {
    return <Navigate to="/customers" replace />;
  }

  return <>{children}</>;
}
